import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { Device as TwilioDevice, Call } from '@twilio/voice-sdk';
import { useCallMode } from '../../hooks/usePlatform';
import {
  IonModal, IonContent, IonFooter,
  IonButton, IonIcon, IonList, IonItem, IonCard,
  IonLabel, IonSkeletonText, IonRow, IonCol, IonGrid, IonText,
  IonRefresherContent, IonAvatar, IonSpinner,
} from '@ionic/react';
import BasePageWithHeader from './../../components/BasePageWithHeader';
import PositionedRefresher from '../PositionedRefresher';
import {
  call, chatbubble, chatbubbleOutline, backspace, chevronBack, chevronDown,
  people, personCircleOutline, keypad as keypadIcon, callOutline, closeOutline, recordingOutline,
  playCircle, pauseCircle,
} from 'ionicons/icons';
import { HiOutlinePhoneArrowDownLeft, HiOutlinePhoneArrowUpRight, HiOutlinePhoneXMark } from 'react-icons/hi2';
import type { CallInvite } from '@capgo/capacitor-twilio-voice';
import { markSmsRead } from '../../hooks/useSmsInbox';
import ContactsListModal from '../ContactsListModal';
import { useAuth } from '../../contexts';
import { INBOX_ROLES, type Role } from '@rsmaids/shared/constants/roles';
import NewContact from '../../pages/bookingSystem/NewContact';
import { MemoryRouter } from 'react-router-dom';
import type { ContactListItem } from '@rsmaids/shared/types/contacts';
import { proxyImage, getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { auth, dbRsRoles, dbTwilio } from '../../auth/firebase';
import { placePhoneCall } from '../../utils/placePhoneCall';
import InboxConversationList from '../InboxConversationList';
import ConversationThread from '../ConversationThread';
import { InboxService } from '@rsmaids/shared/services/api/inboxService';
import styles from './SoftPhoneModal.module.scss';
import { useTranslation } from 'react-i18next';

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  align: 'left' | 'right' | 'center' = 'center',
) {
  const [ready, setReady] = useState(false);
  // Clear readiness the moment the dropdown closes — computed during
  // render rather than as a synchronous setState call at the top of the
  // effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && ready) setReady(false);
  }
  useEffect(() => {
    if (!isOpen) return;
    const computeAndApply = () => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      const card = cardRef.current as HTMLElement | null;
      if (!field || !portal || !card) return;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      card.style.top = `${box.bottom - anchor.top + 4}px`;
      if (align === 'center') {
        const w = card.offsetWidth || 160;
        card.style.left = `${box.left - anchor.left + box.width / 2 - w / 2}px`;
        card.style.right = '';
      } else if (align === 'right') {
        card.style.right = `${anchor.right - box.right}px`;
        card.style.left = '';
      } else {
        card.style.left = `${box.left - anchor.left}px`;
        card.style.right = '';
      }
      setReady(true);
    };
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(computeAndApply));
    const handleOutside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || portalRef.current?.contains(t)) return;
      onClose();
    };
    const doc = globalThis.document;
    doc.addEventListener('pointerdown', handleOutside);
    return () => { cancelAnimationFrame(raf1); doc.removeEventListener('pointerdown', handleOutside); };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, align]);
  return ready;
}

type Tab = 'keypad' | 'recents' | 'messages';

type DeviceState = 'unregistered' | 'registering' | 'registered' | 'error';

interface IncomingCallState { invite: CallInvite; accepting: boolean; }

type CallLog = {
  id: string; number: string; callerName: string;
  direction: string; status: string; agentAnswered: boolean | null; agentName: string;
  duration: number; timestamp: number;
  recordings: Array<{ sid: string; duration: number; source: string; transcriptionText?: string }>;
};

const FUNCTIONS_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL
  || 'https://us-central1-rsmaidsbooking.cloudfunctions.net';

const DIAL_PAD: { d: string; sub: string; glyph?: string; sm?: boolean; subIcon?: string }[][] = [
  [{ d: '1', sub: '', subIcon: 'voicemail' }, { d: '2', sub: 'ABC' }, { d: '3', sub: 'DEF' }],
  [{ d: '4', sub: 'GHI' }, { d: '5', sub: 'JKL' }, { d: '6', sub: 'MNO' }],
  [{ d: '7', sub: 'PQRS' }, { d: '8', sub: 'TUV' }, { d: '9', sub: 'WXYZ' }],
  [{ d: '*', sub: '', glyph: '✱', sm: true }, { d: '0', sub: '+' }, { d: '#', sub: '' }],
];

// Recents icon: Solar "phone-linear" handset + Material Symbols Light "call-received" / "call-made" arrows (paths unmodified, arrows scaled)
const RecentsIcon: React.FC = () => (
  <svg viewBox="2.5 0 26 21.5" fill="none" stroke="currentColor" className={styles.recentsSvg} aria-hidden="true">
    <path strokeWidth="1.15" strokeLinecap="round" d="M18.6763 19.9631C19.1917 19.9122 19.6399 19.6343 20.0011 19.254L21.4217 17.7584C22.3806 16.7489 22.1102 15.0182 20.8833 14.312L18.9728 13.2123C18.1672 12.7486 17.1858 12.8848 16.5562 13.5477L16.1007 14.0272C16.1007 14.0272 15.0181 15.167 12.0631 12.0559C9.10812 8.94484 10.1907 7.80507 10.1907 7.80507L10.4775 7.50311C11.1841 6.75924 11.2507 5.56497 10.6342 4.6931L9.37326 2.90961C8.61028 1.8305 7.13596 1.68795 6.26145 2.60864L4.69185 4.26114C4.25823 4.71766 3.96765 5.30945 4.00289 5.96594C4.09304 7.64546 4.81072 11.259 8.81536 15.4752C13.0621 19.9462 17.0468 20.1239 18.6763 19.9631Z" />
    <path transform="translate(10.8 0.2) scale(0.47)" fill="currentColor" strokeWidth="0.7" d="M6 18V9h1v7.292L18.292 5l.708.708L7.708 17H15v1z" />
    <path transform="translate(17.4 1.3) scale(0.47)" fill="currentColor" strokeWidth="0.7" d="M5.708 19L5 18.292L16.292 7H9V6h9v9h-1V7.708z" />
  </svg>
);

function normalizePhone(phone: string): string {
  const d = phone.replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return phone.startsWith('+') ? phone : `+${d}`;
}

function formatPhone(phone: string): string {
  const d = phone.replace(/\D/g, '');
  const n = d.startsWith('1') && d.length > 10 ? d.slice(1) : d;
  if (n.length <= 3) return n.length ? `(${n}` : '';
  if (n.length <= 6) return `(${n.slice(0, 3)}) ${n.slice(3)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6, 10)}`;
}

// Light haptic tick on keypress. No-op on web / if the plugin isn't available.
async function keyTapHaptic() {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* web or plugin missing — silent */ }
}

// Dial-pad button with an explicit pressed state (Ionic's ::active is
// unreliable under WebView touch) + haptic feedback. Fires on pointer-DOWN so
// the digit registers instantly, the way a native dialer feels.
const DialButton: React.FC<{
  className: string;
  pressedClassName: string;
  ariaLabel: string;
  onPress: () => void;
  children: React.ReactNode;
}> = ({ className, pressedClassName, ariaLabel, onPress, children }) => {
  const [pressed, setPressed] = React.useState(false);
  const firedRef = React.useRef(false);

  const down = () => {
    firedRef.current = true;
    setPressed(true);
    void keyTapHaptic();
    onPress();
  };
  const up = () => setPressed(false);

  return (
    <IonButton
      fill="clear"
      aria-label={ariaLabel}
      className={`${className}${pressed ? ` ${pressedClassName}` : ''}`}
      onPointerDown={down}
      onPointerUp={up}
      onPointerLeave={up}
      onPointerCancel={up}
      // Pointer events cover touch + mouse + pen; guard the synthetic click so
      // the digit isn't entered twice on platforms that still emit both.
      onClick={(e) => {
        if (firedRef.current) { firedRef.current = false; e.preventDefault(); return; }
        down(); up();
      }}
    >
      {children}
    </IonButton>
  );
};

const SoftPhoneModal: React.FC = () => {
  const { t: tr } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('keypad');
  // Owners and managers get the Messages tab (every conversation) and the contact picker. A lead has neither: they can only
  // reply by text to a caller from the call details screen. (Staff do not get the softphone at all; see PhoneHost in App.tsx.)
  const { authState } = useAuth();
  const canSeeInboxList = INBOX_ROLES.includes(authState.user?.role as Role);
  const { isVoipMode, isCallThroughMode } = useCallMode();

  // ── Twilio device (VoIP mode only) ────────────────────────────
  const deviceRef = useRef<InstanceType<typeof TwilioDevice> | null>(null);
  const [deviceState, setDeviceState] = useState<DeviceState>('unregistered');
  const phoneReady = isCallThroughMode || deviceState === 'registered';

  // ── Incoming call (non-native web only) ────────────────────────
  const [incoming, setIncoming] = useState<IncomingCallState | null>(null);
  const listenersRef = useRef<Array<{ remove: () => Promise<void> }>>([]);

  // ── Call / dialer state ────────────────────────────────────────
  const [phoneNumber, setPhoneNumber] = useState('');
  // Name to show above the number on the keypad / active-call view when the
  // number came from a known contact (contact picker, recents, rs:openDialer).
  // Cleared as soon as the user edits digits by hand — a hand-typed number
  // instead resolves via `matchedContactName` below.
  const [dialContactName, setDialContactName] = useState('');
  const [isCallActive, setIsCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  // Phone → name index built once from the same `cachedContacts` localStorage
  // array ContactsListModal uses, so typing a number that belongs to a known
  // contact shows their name. Keyed by the last 10 digits (NANP local number)
  // so formatting / +1 differences don't matter.
  const last10 = (v: string) => v.replace(/\D/g, '').slice(-10);
  const contactPhoneIndex = useMemo((): Map<string, { name: string; picture: string }> => {
    const map = new Map<string, { name: string; picture: string }>();
    try {
      const raw = localStorage.getItem('cachedContacts');
      if (raw) {
        for (const c of JSON.parse(raw) as ContactListItem[]) {
          const key = last10(c.primaryPhone || '');
          const name =
            c.displayName ||
            `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
            '';
          if (key.length === 10 && name && !map.has(key)) map.set(key, { name, picture: proxyImage(c.avatar) || '' });
        }
      }
    } catch { /* ignore bad cache */ }
    return map;
  }, []);
  const matchedContact =
    !dialContactName && phoneNumber
      ? contactPhoneIndex.get(last10(phoneNumber))
      : undefined;
  const matchedContactName = matchedContact?.name || '';
  const displayDialName = dialContactName || matchedContactName;

  // ── Business outgoing number ───────────────────────────────────
  const [businessNumbers, setBusinessNumbers] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('softphone_numbers') ?? '[]'); } catch { return []; }
  });
  const [selectedNumber, setSelectedNumber] = useState<string>(
    () => localStorage.getItem('softphone_selected') ?? ''
  );
  const setNumbers = (nums: string[]) => {
    localStorage.setItem('softphone_numbers', JSON.stringify(nums));
    setBusinessNumbers(nums);
    const pick = nums.length > 1 ? nums[1] : nums[0] ?? '';
    localStorage.setItem('softphone_selected', pick);
    setSelectedNumber(pick);
  };
  const [showNumberPicker, setShowNumberPicker] = useState(false);
  const numberPickerAnchorRef = useRef<HTMLElement | null>(null);
  const numberPickerPortalRef = useRef<HTMLElement | null>(null);
  const numberPickerCardRef = useRef<HTMLElement | null>(null);
  const [numberPickerPortalNode, setNumberPickerPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setNumberPickerPortalNode(numberPickerPortalRef.current);
  }, [showNumberPicker]);
  const closeNumberPicker = React.useCallback(() => setShowNumberPicker(false), []);
  const numberPickerReady = usePortalDropdownPosition(
    showNumberPicker && businessNumbers.length > 1,
    numberPickerAnchorRef,
    numberPickerPortalRef,
    numberPickerCardRef,
    closeNumberPicker,
    'center',
  );

  // ── Messages tab: selected thread (list <-> thread, entirely local, no navigation) ──
  const [selectedThread, setSelectedThread] = useState<{
    id: string;
    contactName?: string;
    avatarUrl?: string;
    initialChannel?: 'chat' | 'email' | 'sms';
    initialFromEmail?: 'scheduling' | 'billing' | 'support';
  } | null>(null);
  // The Messages tab (InboxConversationList) mounts as soon as the softphone opens (hidden unless that tab is
  // showing) and then STAYS mounted while you switch tabs or open a thread — like the Inbox page, which never
  // remounts — so it has already loaded by the time you tap Messages, and its loading and listeners run once
  // instead of on every tab change. Reset when the softphone closes.
  const [messagesMounted, setMessagesMounted] = useState(false);
  const phoneToThreadId = (phone: string) => `sms-${normalizePhone(phone).replace('+', '')}`;

  // ── Call logs ──────────────────────────────────────────────────
  // Latest 100 calls (live from the Firestore cache) + calls older than that (loaded from Twilio on demand)
  const [cachedCalls, setCachedCalls] = useState<CallLog[]>([]);
  const [olderCalls, setOlderCalls] = useState<CallLog[]>([]);
  const [recentsOpened, setRecentsOpened] = useState(false);
  const [logsLoading, setLogsLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [noMoreOlder, setNoMoreOlder] = useState(false);
  const callLogs = useMemo(() => {
    const known = new Set(cachedCalls.map((c) => c.id));
    return [...cachedCalls, ...olderCalls.filter((c) => !known.has(c.id))].sort((x, y) => y.timestamp - x.timestamp);
  }, [cachedCalls, olderCalls]);
  const [selectedLog, setSelectedLog] = useState<CallLog | null>(null);
  // Set by the rs:openMissedCall listener below — consumed once the call appears in the
  // list, to auto-select that call's detail view (see the effect after the recents listener).
  const pendingSelectCallSidRef = useRef<string | null>(null);
  const [playingRecording, setPlayingRecording] = useState<string | null>(null);
  const [recordingBlobUrls, setRecordingBlobUrls] = useState<Record<string, string>>({});
  const [recordingProgress, setRecordingProgress] = useState<Record<string, { current: number; total: number }>>({});
  const audioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const recentsAnchorRef = useRef<HTMLIonRowElement>(null);
  const recentsScrollRef = useRef<HTMLIonGridElement>(null);
  const [recentsRefresherReady, setRecentsRefresherReady] = useState(false);
  // Clear readiness the moment the recents tab is no longer active/visible —
  // computed during render rather than as a synchronous setState call at
  // the top of the effect below.
  const recentsRefresherVisible = activeTab === 'recents' && !selectedLog;
  const [wasRecentsRefresherVisible, setWasRecentsRefresherVisible] = useState(recentsRefresherVisible);
  if (recentsRefresherVisible !== wasRecentsRefresherVisible) {
    setWasRecentsRefresherVisible(recentsRefresherVisible);
    if (!recentsRefresherVisible && recentsRefresherReady) setRecentsRefresherReady(false);
  }
  useEffect(() => {
    if (!recentsRefresherVisible) return;
    const id = requestAnimationFrame(() => setRecentsRefresherReady(true));
    return () => cancelAnimationFrame(id);
  }, [recentsRefresherVisible]);

  // ── Contacts modal ─────────────────────────────────────────────
  const [showContacts, setShowContacts] = useState(false);
  // NewContact is presented as a SIBLING of ContactsListModal (not nested) —
  // a 3rd stacked IonModal renders behind the overlay backdrop → black screen.
  const [showNewContact, setShowNewContact] = useState(false);

  // ── Handlers ───────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPhoneNumber('');
    setDialContactName('');
    setSelectedThread(null);
    setIncoming(null);
    setShowNumberPicker(false);
    setSelectedLog(null);
    setShowContacts(false);
    setShowNewContact(false);
    setMessagesMounted(false);
    setRecentsOpened(false);
    setOlderCalls([]);
    setNoMoreOlder(false);
  }, []);

  // ── rs:openDialer listener ─────────────────────────────────────
  // detail.intent defaults to 'messages' for back-compat with the native Android "Message"
  // button on the incoming-call screen, which has always fired {phone} meaning "open the SMS
  // thread for this number". ConversationThread's call button fires {phone, intent:'keypad'}
  // to prefill the dialpad instead (matching openIvrDialer's desktop semantics).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ phone?: string; intent?: 'keypad' | 'messages'; contactName?: string }>).detail;
      const phone = detail?.phone;
      if (phone && detail?.intent === 'keypad') {
        setDialContactName(detail.contactName || '');
        setPhoneNumber(normalizePhone(phone));
        setActiveTab('keypad');
      } else if (phone) {
        setSelectedThread({ id: phoneToThreadId(phone) });
        setActiveTab('messages');
      } else {
        setActiveTab('keypad');
      }
      setIsOpen(true);
    };
    window.addEventListener('rs:openDialer', handler);
    return () => window.removeEventListener('rs:openDialer', handler);
  }, []);

  // ── rs:openMissedCall listener — fired by MainActivity when a missed-call
  // notification is tapped (see RsmaidsFirebaseMessagingService.java /
  // MainActivity.java). Opens straight to that call's detail view.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ callSid?: string }>).detail;
      const callSid = detail?.callSid;
      if (!callSid) return;
      const cached = callLogs.find(c => c.id === callSid);
      if (cached) {
        setSelectedLog(cached);
      } else {
        // Not loaded yet (cold start) — the effect that watches the call list
        // picks this up once the Recents cache arrives.
        pendingSelectCallSidRef.current = callSid;
      }
      setActiveTab('recents');
      setIsOpen(true);
    };
    window.addEventListener('rs:openMissedCall', handler);
    return () => window.removeEventListener('rs:openMissedCall', handler);
  }, [callLogs]);

  // ── rs:closeDialer listener (e.g. "View Contact" navigating away) ──
  useEffect(() => {
    const handler = () => handleClose();
    window.addEventListener('rs:closeDialer', handler);
    return () => window.removeEventListener('rs:closeDialer', handler);
  }, [handleClose]);

  // ── Incoming call listeners (non-native web only) ──────────────
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    let cancelled = false;
    (async () => {
      const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');
      const h1 = await CapacitorTwilioVoice.addListener('callInviteReceived', async (invite) => {
        if (cancelled) return;
        setIncoming({ invite, accepting: false });
        setIsOpen(true);
        // invite.from is the business Twilio number — look up the active caller info by it.
        try {
          const twilioNumber = invite.from?.replace('client:', '') || '';
          if (twilioNumber) {
            const snap = await getDoc(doc(dbTwilio, 'activeIncomingCall', twilioNumber));
            if (snap.exists()) {
              const d = snap.data() as { callerNumber?: string; callerName?: string; dialedNumber?: string; createdAt?: number };
              // Only use if written within the last 60 seconds (guards against stale data).
              if (d.createdAt && Date.now() - d.createdAt < 60000) {
                const { createdAt: _ts, ...strFields } = d;
                const enriched = { ...invite, customParams: { ...invite.customParams, ...strFields } };
                setIncoming(prev => prev?.invite.callSid === invite.callSid ? { ...prev, invite: enriched } : prev);
              }
            }
          }
        } catch { /* non-fatal */ }
      });
      const h2 = await CapacitorTwilioVoice.addListener('callInviteCancelled', ({ callSid }) => {
        if (!cancelled) setIncoming(prev => prev?.invite.callSid === callSid ? null : prev);
      });
      const h3 = await CapacitorTwilioVoice.addListener('callConnected', () => {
        if (cancelled) return;
        setIncoming(null);
        setIsCallActive(true);
      });
      const h4 = await CapacitorTwilioVoice.addListener('callDisconnected', ({ callSid }) => {
        if (cancelled) return;
        setIncoming(prev => prev?.invite.callSid === callSid ? null : prev);
        setIsCallActive(false);
        setPhoneNumber('');
      });
      listenersRef.current = [h1, h2, h3, h4];
      try {
        const status = await CapacitorTwilioVoice.getCallStatus();
        if (!cancelled && status.pendingInvites.length > 0)
          setIncoming(prev => prev ?? { invite: status.pendingInvites[0], accepting: false });
      } catch { /* not fatal */ }
    })();
    return () => {
      cancelled = true;
      listenersRef.current.forEach(h => h.remove());
      listenersRef.current = [];
    };
  }, []);

  // ── Load business outgoing numbers (on mount — not gated on isOpen) ─
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      try {
        const roleSnap = await getDoc(doc(dbRsRoles, 'Roles', user.uid));
        if (!roleSnap.exists()) return;
        const { phone, wixContactId } = roleSnap.data();
        const normPhone = phone ? phone.replace(/\D/g, '') : null;
        let staffSnap = null;
        if (normPhone) {
          const q = query(collection(dbRsRoles, 'staff'), where('phone', '==', normPhone));
          const res = await getDocs(q);
          if (!res.empty) staffSnap = res.docs[0];
        }
        if (!staffSnap && wixContactId) {
          const q = query(collection(dbRsRoles, 'staff'), where('contactId', '==', wixContactId));
          const res = await getDocs(q);
          if (!res.empty) staffSnap = res.docs[0];
        }
        if (staffSnap) {
          const nums: string[] = staffSnap.data().businessPhoneNumbers || [];
          if (nums.length > 0) setNumbers(nums);
        }
      } catch (e) {
        console.error('[SoftPhone] loadBusinessNumbers:', e);
      }
    });
    return () => unsub();
  }, []);

  // ── Call duration timer ────────────────────────────────────────
  // Reset the duration the moment the call ends — computed during render
  // rather than as a synchronous setState call at the top of the effect
  // below.
  const [wasCallActive, setWasCallActive] = useState(isCallActive);
  if (isCallActive !== wasCallActive) {
    setWasCallActive(isCallActive);
    if (!isCallActive && callDuration !== 0) setCallDuration(0);
  }
  useEffect(() => {
    if (!isCallActive) return;
    const t = setInterval(() => setCallDuration(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [isCallActive]);

  // ── Recents: latest 100 calls live from the Firestore cache, older calls on demand ──
  // The Firestore `recentCalls` collection (written by the refreshCallLogCache Cloud Function, see
  // functions-voice/src/services/recentCallsCache.ts) is read through a live listener, so the list shows at once
  // from the device's own copy and updates itself — nothing is re-read from Twilio when you switch tabs.
  // Twilio is only asked to refresh that cache (on first opening Recents, and on pull-to-refresh) and to page
  // back to calls older than the cache ("Load older calls").
  const refreshCallCache = React.useCallback(async (force = false) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${FUNCTIONS_URL}/refreshCallLogCache${force ? '?force=1' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      console.error('[SoftPhone] refresh call cache:', e);
    }
  }, []);

  // Starts the first time Recents is shown after the softphone opens; handleClose resets it.
  if (isOpen && activeTab === 'recents' && !recentsOpened) setRecentsOpened(true);
  useEffect(() => {
    if (!isOpen || !recentsOpened) return;
    let refreshed = false;
    const unsubscribe = onSnapshot(
      query(collection(dbTwilio, 'recentCalls'), orderBy('timestamp', 'desc'), limit(100)),
      (snap) => {
        setCachedCalls(snap.docs.map((d) => d.data() as CallLog));
        // An empty cache (first ever open) keeps the skeleton until the first refresh has filled it.
        if (!snap.empty || refreshed) setLogsLoading(false);
      },
      (e) => {
        console.error('[SoftPhone] recent calls listener:', e);
        setLogsLoading(false);
      },
    );
    void refreshCallCache().finally(() => { refreshed = true; setLogsLoading(false); });
    return unsubscribe;
  }, [isOpen, recentsOpened, refreshCallCache]);

  // A missed-call notification may name a call that was not loaded yet (see rs:openMissedCall above):
  // open its detail view as soon as it shows up in the list.
  useEffect(() => {
    const sid = pendingSelectCallSidRef.current;
    if (!sid) return;
    const match = callLogs.find((c) => c.id === sid);
    if (match) {
      pendingSelectCallSidRef.current = null;
      setSelectedLog(match);
    }
  }, [callLogs]);

  const loadOlderCalls = React.useCallback(async () => {
    if (loadingOlder) return;
    setLoadingOlder(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const oldest = callLogs.length ? callLogs[callLogs.length - 1].timestamp : Date.now();
      const res = await fetch(`${FUNCTIONS_URL}/getCallLogs?before=${oldest}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const { calls } = await res.json() as { calls: CallLog[] };
      const known = new Set(callLogs.map((c) => c.id));
      const fresh = calls.filter((c) => !known.has(c.id));
      if (fresh.length === 0) setNoMoreOlder(true);
      else setOlderCalls((prev) => [...prev, ...fresh]);
    } catch (e) {
      console.error('[SoftPhone] load older calls:', e);
    } finally {
      setLoadingOlder(false);
    }
  }, [callLogs, loadingOlder]);

  // ── Twilio device init (VoIP / PWA mode only) ─────────────────
  useEffect(() => {
    if (!isOpen || !isVoipMode) return;
    let cancelled = false;
    (async () => {
      try {
        setDeviceState('registering');
        const idToken = await auth.currentUser?.getIdToken();
        const res = await fetch(
          `${FUNCTIONS_URL}/getTwilioVoiceToken?platform=web`,
          { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
        );
        if (!res.ok) throw new Error(`Token fetch failed: ${res.status}`);
        const { token } = await res.json();
        if (cancelled) return;

        const device = new TwilioDevice(token, { maxAverageBitrate: 16000 });
        device.on('registered', () => { if (!cancelled) setDeviceState('registered'); });
        device.on('registering', () => { if (!cancelled) setDeviceState('registering'); });
        device.on('unregistered', () => { if (!cancelled) setDeviceState('unregistered'); });
        device.on('error', () => { if (!cancelled) setDeviceState('error'); });
        device.on('incoming', (call: Call) => {
          if (cancelled) return;
          setPhoneNumber(call.parameters.From ?? '');
          call.accept();
          setIsCallActive(true);
        });
        device.on('disconnect', () => {
          if (cancelled) return;
          setIsCallActive(false);
          setPhoneNumber('');
        });

        await device.register();
        if (!cancelled) deviceRef.current = device;
      } catch (e) {
        if (!cancelled) { setDeviceState('error'); console.error('[SoftPhone] Twilio init:', e); }
      }
    })();
    return () => {
      cancelled = true;
      if (deviceRef.current) {
        deviceRef.current.unregister().catch(() => {});
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      setDeviceState('unregistered');
    };
  }, [isOpen, isVoipMode]);

  // ── Recording playback ────────────────────────────────────────
  useEffect(() => {
    if (playingRecording && recordingBlobUrls[playingRecording]) {
      const audio = audioRefs.current[playingRecording];
      if (audio && audio.paused) audio.play().catch(() => {});
    }
  }, [recordingBlobUrls, playingRecording]);

  const handlePlayRecording = async (sid: string) => {
    if (recordingBlobUrls[sid]) {
      const audio = audioRefs.current[sid];
      if (!audio) return;
      if (audio.paused) { audio.play(); setPlayingRecording(sid); }
      else { audio.pause(); setPlayingRecording(null); }
      return;
    }
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(
        `${FUNCTIONS_URL}/getRecordingAudio?sid=${sid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const blob = await res.blob();
      setRecordingBlobUrls(prev => ({ ...prev, [sid]: URL.createObjectURL(blob) }));
      setPlayingRecording(sid);
    } catch (e) {
      console.error('[SoftPhone] recording:', e);
    }
  };

  const handleCall = async () => {
    if (!phoneNumber.trim()) return;
    try {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch { /* web / plugin missing */ }
    try {
      // Native: isCallActive is driven by the callConnected/callDisconnected
      // listeners above once the VoIP leg actually connects.
      const native = Capacitor.isNativePlatform();
      if (!native) setIsCallActive(true);
      await placePhoneCall(normalizePhone(phoneNumber), selectedNumber, {
        contactName: displayDialName || undefined,
        contactPicture: matchedContact?.picture || undefined,
      });
      if (!native) setIsCallActive(false);
    } catch (e) {
      console.error('[SoftPhone] placePhoneCall:', e);
      if (!Capacitor.isNativePlatform()) setIsCallActive(false);
    }
  };

  const handleAccept = async () => {
    if (!incoming) return;
    setIncoming(prev => prev ? { ...prev, accepting: true } : null);
    try {
      const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');
      await CapacitorTwilioVoice.acceptCall({ callSid: incoming.invite.callSid });
    } catch (e) {
      console.error('[SoftPhone] acceptCall:', e);
      setIncoming(null);
    }
  };

  const handleReject = async () => {
    if (!incoming) return;
    try {
      const { CapacitorTwilioVoice } = await import('@capgo/capacitor-twilio-voice');
      await CapacitorTwilioVoice.rejectCall({ callSid: incoming.invite.callSid });
    } catch (e) {
      console.error('[SoftPhone] rejectCall:', e);
    }
    setIncoming(null);
  };

  const contactDisplayName = (c: ContactListItem): string =>
    c.displayName ||
    `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
    c.primaryEmail ||
    c.primaryPhone ||
    '';

  // Prefill the keypad from a known contact, keeping their name for the display.
  const prefillKeypad = (phone: string, name: string) => {
    setDialContactName(name);
    setPhoneNumber(phone);
    setActiveTab('keypad');
  };

  const openThread = (
    phone: string,
    contact: string,
    opts?: { channel?: 'chat' | 'email' | 'sms'; fromEmail?: 'scheduling' | 'billing' | 'support' },
  ) => {
    setSelectedThread({
      id: phoneToThreadId(phone),
      contactName: contact || undefined,
      initialChannel: opts?.channel,
      initialFromEmail: opts?.fromEmail,
    });
    setActiveTab('messages');
    markSmsRead(phone);
  };

  // ContactsListModal's Message / Email slider buttons: SMS, email and chat are
  // one unified ConversationThread per contact — addressed by the Wix
  // conversation id, not the phone-keyed sms-<digits> thread. Open a temp shell
  // immediately, resolve (or create) the real conversation, then hand the id to
  // ConversationThread via the same `realConversationFound` event
  // InboxConversationList uses.
  const openUnifiedThreadForContact = async (
    contact: ContactListItem,
    opts: { channel: 'chat' | 'email' | 'sms'; fromEmail?: 'scheduling' | 'billing' | 'support' },
  ) => {
    if (!contact.id) return;
    const name =
      contact.displayName ||
      `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
      contact.primaryEmail ||
      contact.primaryPhone ||
      'Contact';
    const tempId = `temp-${Date.now()}`;
    localStorage.setItem(`conversation-${tempId}-contactName`, name);
    setSelectedThread({
      id: tempId,
      contactName: name,
      avatarUrl: contact.avatar || undefined,
      initialChannel: opts.channel,
      initialFromEmail: opts.fromEmail,
    });
    setActiveTab('messages');
    try {
      const conv = await InboxService.startConversation(contact.id);
      if (conv?.id) {
        window.dispatchEvent(new CustomEvent('realConversationFound', {
          detail: { conversationId: String(conv.id), contactName: name },
        }));
      }
    } catch (err) {
      console.error('[SoftPhone] startConversation failed', err);
    }
  };

  const fmtDur = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const incomingParams = incoming?.invite.customParams;
  const getParam = (key: string) => incomingParams instanceof Map ? incomingParams.get(key) : (incomingParams as Record<string, string> | undefined)?.[key];
  const callerNumber = getParam('callerNumber') || incoming?.invite.from || 'Unknown';
  const callerDisplay = getParam('callerName') || callerNumber;
  const inThread = activeTab === 'messages' && !!selectedThread;
  const showMessages = activeTab === 'messages' && !incoming;
  if (isOpen && !messagesMounted && canSeeInboxList) setMessagesMounted(true);

  // ── Render ─────────────────────────────────────────────────────
  return (
    <>
      <IonModal
        isOpen={isOpen}
        onDidDismiss={handleClose}
        className={styles.modal}
        backdropDismiss={!incoming}
      >
        <div className={styles.modalWrapper}>
        {inThread && selectedThread && (
          /* ── In-thread: ConversationThread owns its own header/content/footer ── */
          <ConversationThread
            id={selectedThread.id}
            onBack={() => {
              setSelectedThread(null);
              // Without the Messages list there is nothing to go back to: return to the keypad
              if (!canSeeInboxList) setActiveTab('keypad');
            }}
            initialContactName={selectedThread.contactName}
            initialAvatarUrl={selectedThread.avatarUrl}
            initialChannel={selectedThread.initialChannel}
            initialFromEmail={selectedThread.initialFromEmail}
            wrapInIonPage={false}
            onCallClick={(phone, contactName) => {
              // The thread resolves the number itself (SMS or the contact's phone); an sms-<digits> id is the fallback.
              const number = phone || (selectedThread.id.startsWith('sms-') ? selectedThread.id.replace('sms-', '') : '');
              if (number) prefillKeypad(normalizePhone(number), contactName || selectedThread.contactName || '');
              setSelectedThread(null);
            }}
            onViewContact={(contactId) => window.dispatchEvent(new CustomEvent('rs:viewContact', { detail: { contactId } }))}
          />
        )}
        {/* Everything else stays mounted (hidden) while a thread is open, so coming back costs no reload */}
        <IonGrid className={`${styles.tabPane} ${inThread ? styles.tabPaneHidden : ''}`}>
        <>
        {/* ── Keypad / Recents / incoming call: BasePageWithHeader + non-scrolling IonContent holding an inner scroll grid (Messages tab: InboxConversationList does the same itself) ── */}
        {/* Portal root — fixed viewport overlay, pointer-events:none */}
        <div ref={numberPickerPortalRef as React.RefObject<HTMLDivElement>}
          className={styles.pickerPortalRoot} />

        {/* Portaled number picker dropdown */}
        {showNumberPicker && businessNumbers.length > 1 && numberPickerPortalNode && createPortal(
          <IonList ref={numberPickerCardRef as React.RefObject<HTMLIonListElement>}
            className={styles.numberPickerList}
            style={{ position: 'absolute', visibility: numberPickerReady ? 'visible' : 'hidden' }}
            lines="none"
          >
            {businessNumbers.map(num => (
              <IonItem key={num} button detail={false} lines="none"
                className={`${styles.numberPickerItem} ${num === selectedNumber ? styles.numberPickerItemActive : ''}`}
                onClick={() => { setSelectedNumber(num); setShowNumberPicker(false); }}
              >
                <IonText><p className={styles.numberPickerText}>{formatPhone(num)}</p></IonText>
              </IonItem>
            ))}
          </IonList>,
          numberPickerPortalNode,
        )}

        {/* ── Messages list: InboxConversationList owns its own header/content; kept mounted once opened ── */}
        {messagesMounted && (
          <IonGrid className={`${styles.tabPane} ${showMessages ? '' : styles.tabPaneHidden}`}>
            <InboxConversationList
              onClose={handleClose}
              onSelectConversation={(id, meta) =>
                setSelectedThread({
                  id,
                  contactName: meta.contactName,
                  avatarUrl: meta.avatarUrl,
                  // Forward the channel intent from the "New Chat" picker's
                  // Message / Email slider buttons.
                  initialChannel: meta.initialChannel,
                  initialFromEmail: meta.initialFromEmail,
                })
              }
            />
          </IonGrid>
        )}
        {!showMessages && (
          <BasePageWithHeader
            headerClassName={styles.header}
            toolbarClassName={styles.toolbar}
            titleHeaderClassName={styles.titleHeaderRow}
            title={
              selectedLog ? tr('components.SoftPhoneModal.SoftPhoneModal.callDetails')
                : incoming ? tr('components.SoftPhoneModal.SoftPhoneModal.incomingCall')
                : activeTab === 'keypad' ? ''
                : tr('components.SoftPhoneModal.SoftPhoneModal.recents')
            }
            titleNode={
              activeTab === 'keypad' && !incoming && !selectedLog && selectedNumber ? (
                <IonButton ref={numberPickerAnchorRef as React.RefObject<HTMLIonButtonElement>}
                  fill="clear" className={styles.numberSelector}
                  onClick={() => setShowNumberPicker(v => !v)}>
                  <IonText><p className={`${styles.numberSelectorText} ${phoneReady ? styles.phoneReady : ''}`}>{formatPhone(selectedNumber)}</p></IonText>
                  <IonIcon icon={chevronDown} className={styles.numberSelectorChevron} />
                </IonButton>
              ) : undefined
            }
            startButtons={
              selectedLog ? (
                <IonButton shape="round" className={styles.closeBtn} onClick={() => setSelectedLog(null)}>
                  <IonIcon icon={chevronBack} />
                </IonButton>
              ) : (
                <IonButton shape="round" className={styles.closeBtn} onClick={handleClose}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              )
            }
            endButtons={canSeeInboxList ? (
              <IonButton fill="clear" className={styles.contactsBtn}
                onClick={() => setShowContacts(true)}>
                <IonIcon slot="icon-only" icon={people} />
              </IonButton>
            ) : undefined}
          >
            <IonContent className={styles.content} scrollY={false}>
              {incoming ? (
                <IonGrid className={styles.scrollGrid}>
                  <IonRow className="ion-justify-content-center" style={{ paddingTop: '80px' }}>
                    <IonCol size="12" className="ion-text-center">
                      <IonText><p className={styles.incomingLabel}>{tr('components.SoftPhoneModal.SoftPhoneModal.incomingCall')}</p></IonText>
                    </IonCol>
                  </IonRow>
                  <IonRow className="ion-justify-content-center">
                    <IonCol size="12" className="ion-text-center">
                      <IonText><p className={styles.callerEmoji}>📞</p></IonText>
                    </IonCol>
                  </IonRow>
                  <IonRow className="ion-justify-content-center">
                    <IonCol size="12" className="ion-text-center">
                      <IonText><p className={styles.callerName}>{callerDisplay}</p></IonText>
                      {callerDisplay !== callerNumber && (
                        <IonText><p className={styles.callerNumber}>{callerNumber}</p></IonText>
                      )}
                      {getParam('dialedNumber') && (
                        <IonText><p className={styles.callerNumber} style={{ opacity: 0.6, fontSize: '0.8em', marginTop: 4 }}>{tr('components.SoftPhoneModal.SoftPhoneModal.via')} {getParam('dialedNumber')}</p></IonText>
                      )}
                    </IonCol>
                  </IonRow>
                  <IonRow className="ion-justify-content-center ion-align-items-start"
                    style={{ marginTop: '64px', gap: '56px' }}>
                    <IonCol size="auto" className="ion-text-center">
                      <IonButton className={styles.declineBtn} onClick={handleReject} disabled={incoming.accepting}>
                        <IonIcon slot="icon-only" icon={callOutline} style={{ transform: 'rotate(135deg)' }} />
                      </IonButton>
                      <IonText><p className={styles.callActionLabel}>{tr('components.SoftPhoneModal.SoftPhoneModal.decline')}</p></IonText>
                    </IonCol>
                    <IonCol size="auto" className="ion-text-center">
                      <IonButton className={styles.acceptBtn} onClick={handleAccept} disabled={incoming.accepting}>
                        <IonIcon slot="icon-only" icon={call} />
                      </IonButton>
                      <IonText><p className={styles.callActionLabel}>{tr('components.SoftPhoneModal.SoftPhoneModal.accept')}</p></IonText>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              ) : (
                <>
                  {activeTab === 'recents' && !selectedLog && (
                    <IonRow ref={recentsAnchorRef} className={styles.recentsRefresherAnchor} />
                  )}
                  {activeTab === 'recents' && !selectedLog && recentsRefresherReady && (
                    <PositionedRefresher
                      titleRef={recentsAnchorRef}
                      contentRef={recentsScrollRef}
                      offset={25}
                      id="softphone-recents-refresher"
                      onIonRefresh={(e) => {
                        refreshCallCache(true).finally(() => e.detail.complete());
                      }}
                    >
                      <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
                    </PositionedRefresher>
                  )}
                  {/* ── Keypad tab ──────────────────────────────────── */}
                  {activeTab === 'keypad' && (
                    <IonGrid className={`${styles.keypadCenter} ${styles.scrollGrid}`}>
                      {/* Number display — fixed-height box: the number is anchored to
                          the bottom, the contact-name line sits in a reserved slot
                          above it (absolutely placed) so showing/hiding the name
                          never moves the dial pad. */}
                      <IonRow className="ion-justify-content-center">
                        <IonCol size="12" className={`ion-text-center ${styles.numberDisplayBox}`}>
                          {phoneNumber && displayDialName && (
                            <IonText><p className={styles.dialContactName}>{displayDialName}</p></IonText>
                          )}
                          {phoneNumber ? (
                            <IonText><p className={styles.numberDisplay}>{formatPhone(phoneNumber)}</p></IonText>
                          ) : isCallActive ? (
                            <IonText><p className={styles.callTimer}>{fmtDur(callDuration)}</p></IonText>
                          ) : (
                            <IonText><p className={styles.numberPlaceholder}>{tr('components.SoftPhoneModal.SoftPhoneModal.enterNumber')}</p></IonText>
                          )}
                        </IonCol>
                      </IonRow>

                      {/* Dial pad */}
                      {DIAL_PAD.map((row, ri) => (
                        <IonRow key={ri} style={{ justifyContent: 'center', gap: '24px', marginBottom: '22px' }}>
                          {row.map(({ d, sub, glyph, sm, subIcon }) => (
                            <DialButton
                              key={d}
                              className={styles.dialBtn}
                              pressedClassName={styles.dialBtnPressed}
                              ariaLabel={`Dial ${d}`}
                              onPress={() => { setDialContactName(''); setPhoneNumber(p => p + d); }}
                            >
                              <span className={styles.dialBtnContent}>
                                <span className={`${styles.dialDigit}${sm ? ` ${styles.dialDigitSm}` : ''}`}>{glyph ?? d}</span>
                                {subIcon
                                  ? <IonIcon icon={recordingOutline} className={styles.dialSubIcon} />
                                  : sub && <span className={styles.dialSub}>{sub}</span>}
                              </span>
                            </DialButton>
                          ))}
                        </IonRow>
                      ))}

                      {/* Call + delete row */}
                      <IonRow style={{ justifyContent: 'center', gap: '24px', marginTop: '4px' }}>
                        <span style={{ visibility: phoneNumber.length > 0 ? 'visible' : 'hidden' }}>
                          <DialButton
                            className={styles.deleteBtn}
                            pressedClassName={styles.dialBtnPressed}
                            ariaLabel="Delete last digit"
                            onPress={() => { setDialContactName(''); setPhoneNumber(p => p.slice(0, -1)); }}
                          >
                            <IonIcon slot="icon-only" icon={backspace} />
                          </DialButton>
                        </span>
                        <IonButton fill="clear" className={styles.callBtn}
                          onClick={handleCall} disabled={!phoneNumber.trim()}>
                          <IonIcon slot="icon-only" icon={call} />
                        </IonButton>
                        {/* spacer to keep call btn centered */}
                        <IonButton fill="clear" style={{ visibility: 'hidden', width: '70px', height: '70px' }} />
                      </IonRow>

                    </IonGrid>
                  )}

                  {/* ── Recents list ────────────────────────────────── */}
                  {activeTab === 'recents' && !selectedLog && (
                    <IonGrid ref={recentsScrollRef} className={`${styles.recentsScrollHost} ion-content-scroll-host`}>
                      {logsLoading
                        ? (
                          <IonList className={styles.logList}>
                            {Array.from({ length: 20 }).map((_, i) => (
                              <IonItem key={i} className={styles.logItem} lines="none">
                                <IonSkeletonText animated style={{ width: '36px', height: '36px', borderRadius: '50%' }} slot="start" />
                                <IonLabel>
                                  <IonSkeletonText animated style={{ width: '55%', height: '14px' }} />
                                  <IonSkeletonText animated style={{ width: '35%', height: '11px', marginTop: '4px' }} />
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        )
                        : callLogs.length === 0
                          ? (
                            <IonRow className="ion-justify-content-center" style={{ paddingTop: '64px' }}>
                              <IonCol size="12" className="ion-text-center">
                                <IonText><p className={styles.emptyIcon}>📞</p></IonText>
                                <IonText><p className={styles.emptyText}>{tr('components.SoftPhoneModal.SoftPhoneModal.noRecentCalls')}</p></IonText>
                              </IonCol>
                            </IonRow>
                          )
                          : (() => {
                              const now = new Date();
                              const todayStr = now.toDateString();
                              const yday = new Date(now); yday.setDate(now.getDate() - 1);
                              const yStr = yday.toDateString();

                              const groups: { key: string; label: string; logs: CallLog[] }[] = [];
                              const keyIdx = new Map<string, number>();
                              for (const log of callLogs) {
                                const d = new Date(log.timestamp);
                                const key = d.toDateString();
                                if (!keyIdx.has(key)) {
                                  const label = key === todayStr ? tr('components.SoftPhoneModal.SoftPhoneModal.today')
                                    : key === yStr ? tr('components.SoftPhoneModal.SoftPhoneModal.yesterday')
                                    : d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
                                  keyIdx.set(key, groups.length);
                                  groups.push({ key, label, logs: [] });
                                }
                                groups[keyIdx.get(key)!].logs.push(log);
                              }

                              return (
                                <>
                                  {groups.map(({ label, logs: dayLogs }) => (
                                    <React.Fragment key={label}>
                                      <IonText><p className={styles.logDayLabel}>{label}</p></IonText>
                                      <IonCard className={styles.logDayCard}>
                                        <IonList className={styles.logDayCardList}>
                                          {dayLogs.map((log) => {
                                            const vmRec = log.recordings.find(r => r.source === 'RecordVerb');
                                            const hasVoicemail = Boolean(vmRec);
                                            // Inbound: unanswered when no agent picked up and no voicemail left
                                            const unanswered = log.direction === 'inbound' && log.agentAnswered === false && !hasVoicemail;
                                            const missed = unanswered || hasVoicemail;
                                            const subtitle = hasVoicemail
                                              ? tr('components.SoftPhoneModal.SoftPhoneModal.voicemailS', { duration: vmRec!.duration })
                                              : unanswered ? tr('components.SoftPhoneModal.SoftPhoneModal.unanswered')
                                              : log.agentName || null;
                                            const timeLabel = new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                            // Outbound calls never get a callerName from the backend
                                            // (that's only populated for inbound via CNAM lookup, keyed
                                            // by the caller's number — meaningless for an outbound call
                                            // where `from` is the business's own Twilio number). Resolve
                                            // the recipient's saved contact name locally instead, same
                                            // index the dialpad already uses.
                                            const logDisplayName = log.callerName
                                              || (log.direction === 'outbound' ? contactPhoneIndex.get(last10(log.number))?.name : '')
                                              || '';
                                            return (
                                              <IonItem key={log.id} lines="inset" button detail={false}
                                                className={`${styles.logItem} ${missed ? styles.missed : log.direction === 'outbound' ? styles.outbound : styles.inbound}`}
                                                onClick={() => setSelectedLog(log)}>
                                                <IonRow className={styles.logRow}>
                                                  <IonCol size="auto" className={styles.logIconCol}>
                                                    <IonText className={`${styles.logIcon} ${hasVoicemail ? styles.logIconVoicemail : ''}`}>
                                                      {hasVoicemail
                                                        ? <IonIcon icon={recordingOutline} style={{ fontSize: '20px' }} />
                                                        : missed ? <HiOutlinePhoneXMark size={18} />
                                                        : log.direction === 'outbound' ? <HiOutlinePhoneArrowUpRight size={18} />
                                                        : <HiOutlinePhoneArrowDownLeft size={18} />}
                                                    </IonText>
                                                  </IonCol>
                                                  <IonCol className={styles.logMainCol}>
                                                    <IonText><p className={styles.logPhone}>{logDisplayName || formatPhone(log.number)}</p></IonText>
                                                    {subtitle && <IonText><p className={styles.logMeta}>{subtitle}</p></IonText>}
                                                  </IonCol>
                                                  <IonCol size="auto" className={styles.logTimeCol}>
                                                    <IonText><p className={styles.logTime}>{timeLabel}</p></IonText>
                                                  </IonCol>
                                                </IonRow>
                                              </IonItem>
                                            );
                                          })}
                                        </IonList>
                                      </IonCard>
                                    </React.Fragment>
                                  ))}
                                  {/* The cache holds the latest 100 calls; anything older comes from Twilio on demand */}
                                  {!noMoreOlder && (
                                    <IonRow className="ion-justify-content-center">
                                      <IonButton fill="clear" disabled={loadingOlder} onClick={loadOlderCalls}>
                                        {loadingOlder ? <IonSpinner name="dots" /> : tr('components.SoftPhoneModal.SoftPhoneModal.loadOlderCalls')}
                                      </IonButton>
                                    </IonRow>
                                  )}
                                </>
                              );
                            })()
                      }
                    </IonGrid>
                  )}

                  {/* ── Recents detail view ──────────────────────────── */}
                  {activeTab === 'recents' && selectedLog && (() => {
                    const log = selectedLog;
                    const isMissed = log.status === 'no-answer' || log.status === 'busy';
                    const callTypeLabel = isMissed ? tr('components.SoftPhoneModal.SoftPhoneModal.missedCall') : log.direction === 'outbound' ? tr('components.SoftPhoneModal.SoftPhoneModal.outgoingCall') : tr('components.SoftPhoneModal.SoftPhoneModal.incomingCall2');
                    const durationLabel = log.duration > 0
                      ? `${Math.floor(log.duration / 60)}m ${log.duration % 60}s` : null;
                    const dirClass = isMissed ? styles.missed : log.direction === 'outbound' ? styles.outbound : styles.inbound;
                    // See the Recents list rendering above for why outbound needs this
                    // local fallback — the backend only resolves callerName for inbound.
                    const logDetailContact = log.direction === 'outbound' ? contactPhoneIndex.get(last10(log.number)) : undefined;
                    const logDetailName = log.callerName || logDetailContact?.name || '';
                    return (
                      <IonGrid className={`${styles.detailView} ${styles.scrollGrid}`}>
                        {/* Avatar */}
                        <IonRow className="ion-justify-content-center" style={{ paddingTop: '32px' }}>
                          <IonCol size="12" className="ion-text-center">
                            {logDetailContact?.picture ? (
                              <IonAvatar className={styles.detailAvatar}>
                                <img
                                  src={proxyImage(logDetailContact.picture) || getDefaultAvatar(logDetailName)}
                                  alt=""
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = getDefaultAvatar(logDetailName);
                                  }}
                                />
                              </IonAvatar>
                            ) : logDetailName ? (
                              // Known name, no saved photo — colored initials avatar
                              // (same as ContactsList.tsx's default), not the generic
                              // person icon, which is reserved for a truly unknown number.
                              <IonAvatar className={styles.detailAvatar}>
                                <img src={getDefaultAvatar(logDetailName)} alt="" />
                              </IonAvatar>
                            ) : (
                              <IonText className={styles.detailAvatar}><IonIcon icon={personCircleOutline} /></IonText>
                            )}
                          </IonCol>
                        </IonRow>
                        {/* Name and number under avatar */}
                        {logDetailName && (
                          <IonRow className="ion-justify-content-center" style={{ marginTop: '12px' }}>
                            <IonCol size="12" className="ion-text-center">
                              <IonText><p className={styles.detailName}>
                                {logDetailName.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                              </p></IonText>
                            </IonCol>
                          </IonRow>
                        )}
                        <IonRow className="ion-justify-content-center" style={{ marginTop: logDetailName ? '4px' : '12px' }}>
                          <IonCol size="12" className="ion-text-center">
                            <IonText><p className={styles.detailNumber}>{formatPhone(log.number)}</p></IonText>
                          </IonCol>
                        </IonRow>
                        {/* Action buttons */}
                        <IonRow className="ion-justify-content-center ion-align-items-start"
                          style={{ marginTop: '24px', gap: '40px' }}>
                          <IonCol size="auto" className="ion-text-center">
                            <IonButton fill="clear" className={styles.detailActionBtn}
                              onClick={() => { prefillKeypad(log.number, logDetailName); setSelectedLog(null); }}>
                              <IonIcon slot="icon-only" icon={call} />
                            </IonButton>
                            <IonText><p className={styles.callActionLabel}>{tr('components.SoftPhoneModal.SoftPhoneModal.call')}</p></IonText>
                          </IonCol>
                          <IonCol size="auto" className="ion-text-center">
                            <IonButton fill="clear" className={styles.detailActionBtn}
                              onClick={() => { openThread(normalizePhone(log.number), logDetailName); setSelectedLog(null); }}>
                              <IonIcon slot="icon-only" icon={chatbubble} />
                            </IonButton>
                            <IonText><p className={styles.callActionLabel}>{tr('components.SoftPhoneModal.SoftPhoneModal.message')}</p></IonText>
                          </IonCol>
                        </IonRow>
                        {/* Call info card */}
                        <IonRow style={{ margin: '28px 0 0' }}>
                          <IonCol size="12">
                            <IonGrid className={styles.detailCard}>
                              <IonRow className="ion-align-items-center">
                                <IonCol size="auto">
                                  <IonText className={`${styles.detailCardIcon} ${dirClass}`}>
                                    {isMissed ? <HiOutlinePhoneXMark size={20} />
                                      : log.direction === 'outbound' ? <HiOutlinePhoneArrowUpRight size={20} />
                                      : <HiOutlinePhoneArrowDownLeft size={20} />}
                                  </IonText>
                                </IonCol>
                                <IonCol>
                                  <IonText><p className={styles.detailCardTitle}>{callTypeLabel}</p></IonText>
                                  <IonText><p className={styles.detailCardSub}>
                                    {new Date(log.timestamp).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                                    {' at '}
                                    {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p></IonText>
                                  {durationLabel && <IonText><p className={styles.detailCardSub}>⏱ {durationLabel}</p></IonText>}
                                </IonCol>
                              </IonRow>
                            </IonGrid>
                          </IonCol>
                        </IonRow>
                        {/* Recordings card */}
                        {(log.recordings?.length ?? 0) > 0 && (
                          <IonRow style={{ margin: '12px 0 0' }}>
                            <IonCol size="12">
                              <IonGrid className={styles.detailCard}>
                                {log.recordings.map((rec) => {
                                  const isVoicemail = rec.source === 'RecordVerb';
                                  const prog = recordingProgress[rec.sid];
                                  const isPlaying = playingRecording === rec.sid;
                                  const totalSecs = prog?.total || rec.duration || 0;
                                  const currentSecs = prog?.current || 0;
                                  return (
                                    <React.Fragment key={rec.sid}>
                                    <IonRow style={{ marginTop: '8px' }}>
                                      <IonCol size="12">
                                        <IonText><p className={`${styles.detailCardTitle} ${isVoicemail ? styles.voicemailLabel : ''}`}>
                                          {isVoicemail ? tr('components.SoftPhoneModal.SoftPhoneModal.voicemail') : tr('components.SoftPhoneModal.SoftPhoneModal.callRecording')}
                                        </p></IonText>
                                      </IonCol>
                                    </IonRow>
                                    <IonRow className="ion-align-items-center" style={{ marginTop: '4px', gap: '0' }}>
                                      {recordingBlobUrls[rec.sid] && (
                                        <audio
                                          ref={(el) => { if (el) audioRefs.current[rec.sid] = el; }}
                                          src={recordingBlobUrls[rec.sid]}
                                          onTimeUpdate={(e) => {
                                            const a = e.currentTarget;
                                            setRecordingProgress(prev => ({ ...prev, [rec.sid]: { current: a.currentTime, total: a.duration || 0 } }));
                                          }}
                                          onLoadedMetadata={(e) => {
                                            const a = e.currentTarget;
                                            setRecordingProgress(prev => ({ ...prev, [rec.sid]: { current: 0, total: a.duration || 0 } }));
                                          }}
                                          onEnded={() => {
                                            setPlayingRecording(null);
                                            setRecordingProgress(prev => { const n = { ...prev }; delete n[rec.sid]; return n; });
                                          }}
                                        />
                                      )}
                                      <IonCol size="auto">
                                        <IonButton fill="clear" className={styles.iconBtn} onClick={() => handlePlayRecording(rec.sid)}>
                                          <IonIcon slot="icon-only" icon={isPlaying ? pauseCircle : playCircle} style={{ fontSize: '2rem', color: 'var(--ion-color-primary)' }} />
                                        </IonButton>
                                      </IonCol>
                                      <IonCol>
                                        <input
                                          type="range"
                                          className={styles.recordingScrub}
                                          min={0} max={totalSecs || 1} step={0.1} value={currentSecs}
                                          onChange={(e) => {
                                            const a = audioRefs.current[rec.sid];
                                            if (a) a.currentTime = parseFloat(e.target.value);
                                          }}
                                        />
                                        <IonText><p className={styles.recordingTime}>{fmtDur(Math.floor(currentSecs))} / {fmtDur(Math.floor(totalSecs))}</p></IonText>
                                      </IonCol>
                                    </IonRow>
                                    {rec.transcriptionText && (
                                      <IonRow style={{ marginTop: '10px' }}>
                                        <IonCol size="12">
                                          <IonText><p className={styles.transcriptLabel}>{tr('components.SoftPhoneModal.SoftPhoneModal.transcript')}</p></IonText>
                                          <IonText><p className={styles.transcriptText}>{rec.transcriptionText}</p></IonText>
                                        </IonCol>
                                      </IonRow>
                                    )}
                                    </React.Fragment>
                                  );
                                })}
                              </IonGrid>
                            </IonCol>
                          </IonRow>
                        )}
                      </IonGrid>
                    );
                  })()}

                </>
              )}
            </IonContent>
          </BasePageWithHeader>
        )}

        {/* ── Footer: tab pill ── */}
        {!incoming && (
          <IonFooter className={styles.tabFooter}>
            <IonGrid className={styles.tabPill}>
              <IonRow className={styles.tabPillRow}>
                <IonCol size={canSeeInboxList ? '4' : '6'} className={styles.tabPillCol}>
                  <IonButton fill="clear" className={`${styles.tabBtn} ${activeTab === 'keypad' ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('keypad')}>
                    <IonLabel><IonIcon icon={keypadIcon} /><IonText><p>{tr('components.SoftPhoneModal.SoftPhoneModal.keypad')}</p></IonText></IonLabel>
                  </IonButton>
                </IonCol>
                <IonCol size={canSeeInboxList ? '4' : '6'} className={styles.tabPillCol}>
                  <IonButton fill="clear" className={`${styles.tabBtn} ${activeTab === 'recents' ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('recents')}>
                    <IonLabel><RecentsIcon /><IonText><p>{tr('components.SoftPhoneModal.SoftPhoneModal.recents')}</p></IonText></IonLabel>
                  </IonButton>
                </IonCol>
                {canSeeInboxList && <IonCol size="4" className={styles.tabPillCol}>
                  <IonButton fill="clear" className={`${styles.tabBtn} ${activeTab === 'messages' ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('messages')}>
                    <IonLabel><IonIcon icon={chatbubbleOutline} /><IonText><p>{tr('components.SoftPhoneModal.SoftPhoneModal.messages')}</p></IonText></IonLabel>
                  </IonButton>
                </IonCol>}
              </IonRow>
            </IonGrid>
          </IonFooter>
        )}
        </>
        </IonGrid>
        </div>
      </IonModal>

      {/* Always mounted — toggled via isOpen so Ionic runs its present/dismiss
          transitions (mounting an IonModal already-open breaks nested child
          modals like NewContact → black screen). */}
      <ContactsListModal
        isOpen={showContacts}
        onClose={() => setShowContacts(false)}
        onSelectContact={(contact: ContactListItem) => {
          const phone = contact.primaryPhone || '';
          if (phone) {
            if (activeTab === 'messages') {
              openThread(normalizePhone(phone), contactDisplayName(contact));
            } else {
              prefillKeypad(phone, contactDisplayName(contact));
            }
          }
          setShowContacts(false);
        }}
        onContactCall={(contact) => {
          const phone = contact.primaryPhone || '';
          if (phone) prefillKeypad(phone, contactDisplayName(contact));
          setShowContacts(false);
        }}
        onContactMessage={(contact, channel, fromEmail) => {
          void openUnifiedThreadForContact(contact, { channel, fromEmail });
          setShowContacts(false);
        }}
        onAddContact={() => { setShowContacts(false); setShowNewContact(true); }}
        title={tr('components.SoftPhoneModal.SoftPhoneModal.selectContact')}
      />

      {showNewContact && (
        <IonModal isOpen={showNewContact} onDidDismiss={() => setShowNewContact(false)}>
          {/* SoftPhoneModal is mounted OUTSIDE <IonReactRouter> (see App.tsx),
              so NewContact's useParams/useLocation/useIonRouter have no router
              context and throw → black screen. A throwaway MemoryRouter gives
              them one; NewContact only uses the router for a back-nav that the
              onClose prop already overrides here. */}
          <MemoryRouter initialEntries={['/tabs/contact/new']}>
            <NewContact onClose={() => setShowNewContact(false)} />
          </MemoryRouter>
        </IonModal>
      )}
    </>
  );
};

export default SoftPhoneModal;
